import { ShareTone } from "@boo/shared";
import { formatBaht } from "./billMath";

interface ShareMessageInput {
  title: string;
  total: number;
  finalized: boolean;
  link: string;
  tone: ShareTone;
  /** Saved per-group wording with {title} {total} {link}; overrides the tone preset. */
  template?: string | null;
}

interface TemplateValues {
  title: string;
  total: number;
  link: string;
}

export const TEMPLATE_PLACEHOLDERS = "{title} {total} {link}";

/** Built-in wording for a tone, as a template. */
export function defaultTemplate(tone: ShareTone, finalized: boolean): string {
  if (tone === ShareTone.Playful) {
    return finalized
      ? `🍽️ บิล "{title}" สรุปแล้วจ้าา มาดูว่าใครต้องควักเท่าไหร่ 😝\n👇\n{link}\nโอนเสร็จอย่าลืมแนบสลิปด้วยน้า ไม่งั้นตามทวงนะ 💸`
      : `🍽️ บิล "{title}" รวม {total} มาแล้ว! 🔥\nใครกินอะไรรีบเข้าไปติ๊กเลย อย่าให้ต้องมาตามนะ 😏\n👇\n{link}\nติ๊กเสร็จกดยืนยันยอดด้วยล่ะ 🙌`;
  }
  return finalized
    ? `สรุปบิล "{title}" เรียบร้อยแล้ว\nสามารถดูยอดของตนเองและโอนได้ที่ลิงก์นี้\n{link}\nโอนเสร็จแล้วกรุณาแนบสลิปในลิงก์ ขอบคุณครับ/ค่ะ`
    : `หารบิล "{title}" (รวม {total})\nกรุณาเลือกรายการของตนเองที่ลิงก์นี้\n{link}\nเลือกเสร็จแล้วกรุณากดยืนยันยอดด้วย ขอบคุณครับ/ค่ะ`;
}

export function fillTemplate(
  template: string,
  { title, total, link }: TemplateValues,
): string {
  return template
    .replaceAll("{title}", () => title)
    .replaceAll("{total}", () => formatBaht(total))
    .replaceAll("{link}", () => link);
}

/** Best-effort reverse of fillTemplate: swap this bill's values back to placeholders. */
export function toTemplate(
  text: string,
  { title, total, link }: TemplateValues,
): string {
  let out = text.replaceAll(link, () => "{link}");
  out = out.replaceAll(formatBaht(total), () => "{total}");
  if (title) out = out.replaceAll(title, () => "{title}");
  return out;
}

export function buildShareMessage({
  title,
  total,
  finalized,
  link,
  tone,
  template,
}: ShareMessageInput): string {
  return fillTemplate(template || defaultTemplate(tone, finalized), {
    title,
    total,
    link,
  });
}

/** Group whose members include every non-owner Friend on the bill, if any. */
export function matchGroupId(
  friendIds: string[],
  groups: { id: string; members: { friend: { id: string } }[] }[],
): string {
  if (friendIds.length === 0) return "";
  const match = groups.find((g) => {
    const members = new Set(g.members.map((m) => m.friend.id));
    return friendIds.every((id) => members.has(id));
  });
  return match?.id ?? "";
}
